import { ValidationError } from '@domain/errors';
import type { AdminAccount, AdminDraft } from '@domain/entities';
import type { AdminRepository } from '@domain/repositories';
import type { AccountStatus } from '@domain/value-objects';
import type { AccessPolicy } from '../../ports';
import type { UseCase } from '../use-case';

/** L'écran entier est réservé au super-admin : le garde est posé dès la lecture. */
export class ListAdmins implements UseCase<void, AdminAccount[]> {
  constructor(
    private readonly repo: AdminRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(): Promise<AdminAccount[]> {
    this.policy.assertSuperAdmin('consulter les comptes admin');
    return this.repo.list();
  }
}

export interface SaveAdminInput {
  id: string | null;
  draft: AdminDraft;
}

export class SaveAdmin implements UseCase<SaveAdminInput, AdminAccount> {
  constructor(
    private readonly repo: AdminRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, draft }: SaveAdminInput): Promise<AdminAccount> {
    this.policy.assertSuperAdmin('gérer les comptes admin');
    const fields: Record<string, string> = {};
    if (!draft.name.trim()) fields.name = 'Le nom est requis';
    if (!draft.email.trim()) fields.email = "L'email est requis";
    if (Object.keys(fields).length > 0) {
      throw new ValidationError('Formulaire incomplet', fields);
    }
    const normalized: AdminDraft = {
      ...draft,
      name: draft.name.trim(),
      email: draft.email.trim().toLowerCase(),
    };
    return id === null ? this.repo.create(normalized) : this.repo.update(id, normalized);
  }
}

export class SetAdminStatus implements UseCase<{ id: string; status: AccountStatus }, AdminAccount> {
  constructor(
    private readonly repo: AdminRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute({ id, status }: { id: string; status: AccountStatus }): Promise<AdminAccount> {
    this.policy.assertSuperAdmin('modifier un compte admin');
    return this.repo.setStatus(id, status);
  }
}

export class ToggleAdminRole implements UseCase<string, AdminAccount> {
  constructor(
    private readonly repo: AdminRepository,
    private readonly policy: AccessPolicy,
  ) {}

  async execute(id: string): Promise<AdminAccount> {
    this.policy.assertSuperAdmin('modifier un rôle');
    return this.repo.toggleRole(id);
  }
}
